"use client";

import { useEffect, useState } from "react";

const INSTALLED_KEY = "mistry-pwa-installed";
const DISMISSED_KEY = "mistry-pwa-dismiss-dismissed-until";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(display-mode: standalone)").matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISSED_KEY) || 0);
    return until > Date.now();
  } catch {
    return false;
  }
}

function markInstalled() {
  try {
    localStorage.setItem(INSTALLED_KEY, "1");
    localStorage.removeItem(DISMISSED_KEY);
  } catch {
    // ignore
  }
}

export default function PwaInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      markInstalled();
      return;
    }
    try {
      if (localStorage.getItem(INSTALLED_KEY) === "1" || isDismissed()) return;
    } catch {
      // ignore
    }

    const onInstalled = () => {
      markInstalled();
      setVisible(false);
      setPrompt(null);
    };

    const onPrompt = (event: Event) => {
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
      setIosHint(false);
      setVisible(true);
    };

    window.addEventListener("appinstalled", onInstalled);
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIos() && !isStandalone()) {
      setIosHint(true);
      setVisible(true);
    }

    return () => {
      window.removeEventListener("appinstalled", onInstalled);
      window.removeEventListener("beforeinstallprompt", onPrompt);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now() + 3 * 24 * 60 * 60 * 1000));
    } catch {
      // ignore
    }
    setVisible(false);
  };

  const install = async () => {
    if (iosHint) {
      dismiss();
      return;
    }
    if (!prompt) return;
    setBusy(true);
    try {
      await prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice.outcome === "accepted") {
        markInstalled();
        setVisible(false);
      }
    } catch {
      // browser blocked or cancelled
    } finally {
      setBusy(false);
      setPrompt(null);
    }
  };

  if (!visible) return null;

  return (
    <div className="pwa-install-banner" role="dialog" aria-label="Install Mistry Bhai app">
      <button type="button" className="pwa-install-body" onClick={() => void install()} disabled={busy}>
        <span className="pwa-install-icon">⬇</span>
        <span className="pwa-install-text">
          <b>{iosHint ? "Add Mistry Bhai to Home Screen" : "Install Mistry Bhai app"}</b>
          <small>
            {iosHint
              ? "Tap Share → Add to Home Screen for quick SOS alerts"
              : "Download for faster alerts & home-screen access"}
          </small>
        </span>
        <span className="pwa-install-go">{busy ? "…" : iosHint ? "Got it" : "Install"}</span>
      </button>
      <button type="button" className="pwa-install-close" aria-label="Dismiss install banner" onClick={dismiss}>×</button>
    </div>
  );
}
